/* script */
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

// Utils
import { cloneDeep } from 'lodash';

// Components
import BaseControl from 'shared/ui/components/form-components/BaseControl.vue';
import Checkbox from 'shared/ui/components/form-components/Checkbox.vue';
import DatetimePicker from 'shared/ui/components/form-components/pickers/DatetimePicker.vue';

const __vue_script__ = {
    name: 'LocationTimeSlots',

    components: {
        Checkbox,
        DatetimePicker
    },

    extends: BaseControl,

    props: {
        value: {
            type: Array,
            default: () => []
        }
    },

    data() {
        return {
            dateFormat: 'YYYY-MM-DD',
            copyTimes: false
        };
    },

    computed: {
        slots: {
            get() {
                const slots = [];
                for (const value of this.value ?? []) {
                    const slot = {
                        timetable: [],
                        days: [value.start_date, value.end_date],
                    };

                    for (const time of value.time_slots) {
                        slot.timetable.push({ time: [time.start_time, time.end_time === '00:00' ? '23:59' : time.end_time] });
                    }

                    slots.push(slot);
                }

                return slots;
            },

            set(value) {
                this.$emit('input', this.slotsToDataModel(value));
            }
        }
    },

    methods: {
        /**
         * Converts the given slots into the data model the package expects.
         *
         * @param {Array} slots - The array of slots to be converted.
         *
         * @returns {Object} The data model representation of the slots.
         */
        slotsToDataModel(slots) {
            slots.sort((a, b) => a.days[0] > b.days[0] ? 1 : -1);

            const data = [];
            for (const slot of slots) {
                const [start_date, end_date] = slot.days;
                const time_slots = slot.timetable
                    .sort((a, b) => a.time[0] > b.time[0] ? 1 : -1)
                    .map(time => {
                        return {
                            start_time: time.time[0],
                            end_time: time.time[1] === '23:59' ? '00:00' : time.time[1]
                        };
                    });

                data.push({ start_date, end_date, time_slots });
            }

            return data;
        },

        /**
         * Determines if a new time slot can be added at the specified index.
         *
         * @param {number} i - The index to check if a new time slot can be added.
         *
         * @returns {boolean} - Returns true if a new time slot can be added, otherwise false.
         */
        canAddTimeSlot(i) {
            const lastTime = this.getEndTimeAt(i, this.slots[i].timetable.length - 1);
            const [hour, minutes] = lastTime.split(':');
            return hour !== '23' || minutes !== '59';
        },

        /**
         * Adds a tooltip to the time slot at the specified index.
         *
         * @param {number} i - The index of the time slot to which the tooltip will be added.
         */
        addTimeSlotTooltip(i) {
            if (!this.canAddTimeSlot(i)) {
                return this.$t('meetings.settings.locations.actions.add_time_slot_disabled');
            }
            return null;
        },

        /**
         * Adds a new time slot at the specified index.
         *
         * @param {number} i - The index at which to add the new time slot.
         */
        addTimeSlot(i) {
            const slots = cloneDeep(this.slots);
            const startTimeIndex = slots[i].timetable.length - 1;
            const startTime = this.getEndTimeAt(i, startTimeIndex);

            slots[i].timetable.push({ time: [startTime, '00:00'] });
            this.slots = slots;
        },

        /**
         * Sets the time slot for a specific location and time.
         *
         * @param {number} i - The index of the location.
         * @param {number} j - The index of the time slot.
         * @param {string} time - The time to set for the specified slot.
         */
        setTimeSlot(i, j, time) {
            const slots = cloneDeep(this.slots);
            slots[i].timetable[j] = { time };
            this.slots = slots;
        },

        /**
         * Retrieves the time at a specific index.
         *
         * @param {number} i - The index of the time slot.
         * @param {number} j - The index within the time slot.
         *
         * @returns {string} The time at the specified index.
         */
        getEndTimeAt(i, j) {
            return this.slots[i].timetable[j].time[1];
        },

        /**
         * Removes a time slot from the list of time slots.
         *
         * @param {number} i - The index of the location.
         * @param {number} j - The index of the time slot to be removed.
         */
        removeTimeSlot(i, j) {
            const slots = cloneDeep(this.slots);
            slots[i].timetable.splice(j, 1);
            this.slots = slots;
        },

        /**
         * Adds a new date slot to the list of available time slots.
         */
        addDateSlot() {
            const slots = cloneDeep(this.slots);
            const now = this.$utils.time.now().format(this.dateFormat);

            const lastSlot = slots[slots.length - 1] || { days: [now, now] };
            const newSlot = {
                timetable: [{ time: ['00:00', '00:00'] }],
                days: [lastSlot.days[1], lastSlot.days[1]],
            };

            if (this.copyTimes) {
                newSlot.timetable = lastSlot.timetable.map(time => {
                    return { time: [...time.time] };
                });
            }

            slots.push(newSlot);
            this.slots = slots;
        },

        /**
         * Sets the date slot for a given index and number of days.
         *
         * @param {number} i - The index of the date slot to set.
         * @param {number} days - The number of days to set for the date slot.
         */
        setDateSlot(i, days) {
            const slots = cloneDeep(this.slots);
            slots[i].days = days;
            this.slots = slots;
        },

        /**
         * Removes a date slot from the list of date slots.
         *
         * @param {number} i - The index of the date slot to be removed.
         */
        removeDateSlot(i) {
            const slots = cloneDeep(this.slots);
            slots.splice(i, 1);
            this.slots = slots;
        },

        /**
         * Checks if the given date is invalid for the specified index.
         *
         * @param {number} i - The index to check.
         * @param {Date} date - The date to validate.
         *
         * @returns {boolean} - Returns true if the date is invalid, otherwise false.
         */
        invalidDates(i, date) {
            for (const [index, day] of this.slots.entries()) {
                if (i === index) {
                    continue;
                }

                const [start, end] = day.days;
                if (this.$utils.time.dateIsBetween(date, start, end, { includeStart: true, includeEnd: true })) {
                    return true;
                }
            }

            return false;
        },

        /**
         * Checks if the given time slot is invalid.
         *
         * @param {number} i - The index of the time slot.
         * @param {number} j - The index of the sub-time slot.
         * @param {Date} date - The date to check the time slot against.
         *
         * @returns {boolean} - Returns true if the time slot is invalid, otherwise false.
         */
        invalidTime(i, j, date) {
            const day = this.slots[i];
            for (const [index, { time }] of day.timetable.entries()) {
                if (j === index) {
                    continue;
                }

                const [start, end] = time.map(t => this.timeToDate(t, date));
                if (this.$utils.time.dateIsBetween(date, start, end, { includeStart: true, includeEnd: false })) {
                    return true;
                }
            }

            return false;
        },

        /**
         * Converts a given time to a Date object based on the provided date.
         *
         * @param {string} time - The time string to convert (e.g., "14:30").
         * @param {Date} date - The base date to use for conversion.
         *
         * @returns {Date} - A new Date object with the time set to the provided time.
         */
        timeToDate(time, date) {
            const [hour, minutes] = time.split(':');
            const datetime = date ? new Date(date.getTime()) : new Date();
            datetime.setHours(hour, minutes);
            return datetime;
        },

        daysLabel(i) {
            let labelWithIndex = this.$t('meetings.form.location_date_time_slot', [` ${i + 1}`]);
            let labelWithoutIndex = this.$t('meetings.form.location_date_time_slot', ['']);

            if (i === 0 && this.slots.length === 1) {
                return `${labelWithoutIndex} *`;
            } else if (i === 0) {
                return `${labelWithIndex} *`;
            } else {
                return labelWithIndex;
            }
        }
    }
};

/* template */
var __vue_render__ = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('control-decorator',{staticClass:"location-time-slots",attrs:{"tip":_vm.tip,"uid":_vm.uid,"errors":_vm.errors,"warning":_vm.warning}},[_vm._l((_vm.slots),function(slot,i){return _c('div',{staticClass:"slots"},[_c('datetime-picker',{attrs:{"label":_vm.daysLabel(i),"clearable":false,"disabled":_vm.disabled,"hint":i === 0 ? _vm.hint : null,"hint-after-label":true,"invalid-dates":function (date) { return _vm.invalidDates(i, date); },"range":true,"timezone":_vm.event.timezone,"value":slot.days,"format":_vm.dateFormat,"name":"days","type":"date","value-type":"format"},on:{"input":function($event){return _vm.setDateSlot(i, $event)}},scopedSlots:_vm._u([{key:"actions",fn:function(ref){return [(i !== 0)?_c('button',{staticClass:"btn btn-icon single danger",attrs:{"type":"button","aria-label":_vm.$t('general.delete')},on:{"click":function($event){return _vm.removeDateSlot(i)}}},[_c('i',{staticClass:"ic-trash-2"})]):_vm._e()]}}],null,true)}),_c('div',{staticClass:"time-slots"},[_vm._l((slot.timetable),function(table,j){return _c('div',{staticClass:"feature-dependant"},[_c('datetime-picker',{attrs:{"clearable":false,"disabled":_vm.disabled,"invalid-times":function (date) { return _vm.invalidTime(i, j, date); },"range":true,"timezone":_vm.event.timezone,"minute-options":['00', '10', '20', '30', '40', '50', '59'],"value":table.time,"format":"HH:mm","name":"time","type":"time","value-type":"format"},on:{"input":function($event){return _vm.setTimeSlot(i, j, $event)}},scopedSlots:_vm._u([{key:"actions",fn:function(ref){return [(j !== 0)?_c('button',{staticClass:"btn btn-icon single danger",attrs:{"type":"button","aria-label":_vm.$t('general.delete')},on:{"click":function($event){return _vm.removeTimeSlot(i, j)}}},[_c('i',{staticClass:"ic-trash-2"})]):_vm._e()]}}],null,true)})],1)}),_c('button',{directives:[{name:"tooltip",rawName:"v-tooltip",value:(_vm.addTimeSlotTooltip(i)),expression:"addTimeSlotTooltip(i)"}],staticClass:"btn btn-icon single",attrs:{"type":"button","aria-label":_vm.$t('general.add'),"disabled":!_vm.canAddTimeSlot(i)},on:{"click":function($event){return _vm.addTimeSlot(i)}}},[_c('i',{staticClass:"ic-plus"})])],2)],1)}),_c('div',{staticClass:"range-actions"},[_c('button',{staticClass:"btn btn-ctx-icon",attrs:{"type":"button"},on:{"click":_vm.addDateSlot}},[_vm._v(_vm._s(_vm.$t('meetings.settings.locations.actions.add_range'))),_c('i',{staticClass:"ic-plus"})]),(_vm.slots.length > 0)?_c('checkbox',{attrs:{"name":"copy_times","label":_vm.$t('meetings.settings.locations.actions.copy_times')},model:{value:(_vm.copyTimes),callback:function ($$v) {_vm.copyTimes=$$v},expression:"copyTimes"}}):_vm._e()],1)],2)}
var __vue_staticRenderFns__ = []

  /* style */
  const __vue_inject_styles__ = undefined
  /* scoped */
  const __vue_scope_id__ = undefined
  /* module identifier */
  const __vue_module_identifier__ = undefined
  /* functional template */
  const __vue_is_functional_template__ = false
  /* component normalizer */
  function __vue_normalize__(
    template, style, script,
    scope, functional, moduleIdentifier, shadowMode,
    createInjector, createInjectorSSR, createInjectorShadow
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {}

    // For security concerns, we use only base name in production mode.
    component.__file = "LocationTimeSlots.vue"

    if (!component.render) {
      component.render = template.render
      component.staticRenderFns = template.staticRenderFns
      component._compiled = true

      if (functional) component.functional = true
    }

    component._scopeId = scope

    if (false) {
      let hook
      if (false) {
        // In SSR.
        hook = function(context) {
          // 2.3 injection
          context =
            context || // cached call
            (this.$vnode && this.$vnode.ssrContext) || // stateful
            (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext) // functional
          // 2.2 with runInNewContext: true
          if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
            context = __VUE_SSR_CONTEXT__
          }
          // inject component styles
          if (style) {
            style.call(this, createInjectorSSR(context))
          }
          // register component module identifier for async chunk inference
          if (context && context._registeredComponents) {
            context._registeredComponents.add(moduleIdentifier)
          }
        }
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        component._ssrRegister = hook
      }
      else if (style) {
        hook = shadowMode 
          ? function(context) {
              style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot))
            }
          : function(context) {
              style.call(this, createInjector(context))
            }
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context)
            return originalRender(h, context)
          }
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook]
        }
      }
    }

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = /*#__PURE__*/__vue_normalize__(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  )

  export default __vue_component__